import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { BadgeCheck } from 'lucide-react';
import * as React from 'react';

import { HelpPopover, NotPermitted } from '@/design';
import { ApiError } from '@/domains/api';
import { idTypeLabel } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type {
  IAdminParticipantDetail,
  IAdminParticipantFact,
  IAdminParticipantHub,
  IAdminParticipantIdentifier,
  IAdminParticipantPendingLookup,
  IParticipantNumber
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent } from '../../components/account-facts';
import { CopyableValue } from '../../components/copyable-value';
import { openFolds } from '../../components/section-nav';
import { TableFrame } from '../../components/table-frame';
import { refusalMessage, statusRefusal } from '../../refusal';
import {
  REFRESH_OF_ID_TYPE,
  VERIFIABLE_ID_TYPES,
  answersFor,
  askedAs,
  registryLabel,
  registryNumbers
} from '../../registries';
import { NoteLine, OUT_OF_DATE } from './said';

/**
 * One member's numbers — two tables, **registry numbers** and **internal ids** (R8) — each
 * registry number with its registries' last answers and their age (a CRD's BrokerCheck's and,
 * since SR-396, IAPD's beside it), and the two writes it carries, "Ask … again" and "Verify
 * number", and the header's "Ask registries again", which makes the first press on every number
 * of the household. The answers themselves are read under Registry answers
 * (`registry-answers.tsx`), which offers the number's "Ask … again" again (`NumberRefresh`). What each press was told is held by the page, keyed by
 * `(participantId, idType, value)` (`useNumberNotes`), not by the button: the header's press, a
 * number's own and the one under its answers then say what came back beside the number, and a
 * note outlives the re-read that takes its number away. One file because those parts share that
 * state.
 *
 * **Which table a number is in is this app's rule** — `VERIFIABLE_ID_TYPES`, the owner's ruling
 * that staff verify registry numbers only, and not a mirror of anything survey-api holds (it
 * stamps any type). An internal id — an `EMAIL`, a migrated `LEGACY_USER_ID` — is this system's
 * own record, so it is drawn with where it came from and no button. The row was a block of four
 * facts and two buttons with their sentences; a migrated person holds five or six ids, and the
 * block made the one fact that mattered — nothing verified — the hardest to find (the
 * assessment's §1.3).
 *
 * **Every press here is `responses.annotate`** (SR-228), read once by the page into
 * `INumberWrites.permitted`: without it each button is drawn off under a `NotPermitted` sentence —
 * one above a member's registry numbers, one beside a number's press under its answers, one beside
 * the header's — and every fact stays.
 */

/**
 * A lookup is shown on the number it asks about — matched on the pair, as R9 has it, rather
 * than on `factKey`, which would need the idType → key mapping a second time. The number's half
 * of the pair is the type the registry is **asked** it as (`askedAs`): a team's `TEAM_LEAD_CRD`
 * is asked of FINRA as a `CRD`, and its lookup names that.
 */
function asksAbout(lookup: IParticipantNumber, identifier: IParticipantNumber): boolean {
  return lookup.idType === askedAs(identifier.idType) && lookup.value === identifier.value;
}

/** The same number as held — what a note is keyed by (`useNumberNotes`), and never a lookup. */
function sameNumber(a: IParticipantNumber, b: IParticipantNumber): boolean {
  return a.idType === b.idType && a.value === b.value;
}

/** What a number's two presses need from the page — the notes it holds, and the re-reads. */
export interface INumberWrites {
  said: Record<string, INumberNotes>;
  say: SayNumber;
  /** The header's "Ask registries again" is going through the numbers: a number's own button
   *  waits for it, rather than racing it with a second press on the same number. */
  askingAll: boolean;
  callerEmail: string;
  watching: boolean;
  /** A press on "Ask … again": the window restarted, and the hub, the activity and every
   *  account read again — it is a write. */
  onRecheck: () => Promise<boolean>;
  /** "Check again" on a waiting lookup: the window restarted and the hub read again, alone — the
   *  poll's hand-driven twin, which writes nothing. */
  onCheckAgain: () => Promise<boolean>;
  onVerified: () => Promise<boolean>;
  /** Whether the reader holds `responses.annotate`, which both presses are (SR-228). */
  permitted: boolean;
}

/** What a number's presses are, for the `NotPermitted` sentence above its table. */
const NUMBER_WRITES = 'asking a registry again or verifying a number';

/**
 * The numbers, and for each registry number where it stands:
 * the registries it is checked against, their last answers and their age, whether
 * anybody corroborated it, what its owner said about it, and what is waiting on it.
 *
 * Verification and the owner's answer are two facts, side by side, and neither is read as the
 * other: `verified_at` means checked against something that is not the owner's word — a
 * registry match somebody confirmed, or a member of staff — while the owner's own "that is
 * mine" verifies nothing. A denial is the loud one, because it is a queue: an owner said the
 * product holds a number that is not theirs. And a registry's `ok` verifies nothing either: it
 * is evidence for staff to read, and "Verify number" is what records that they did.
 *
 * A lookup waiting on a number this participant no longer holds is still shown, below the
 * tables, rather than dropped with the number — and so is what a press on such a number was
 * told: the refusal is the one thing on the page that says why the number went.
 */
export function MemberNumbers({
  participant,
  headingLevel,
  writes
}: {
  participant: IAdminParticipantDetail;
  /** 3 under the section's own heading; 4 under a member's, in a household of more than one. */
  headingLevel: 3 | 4;
  writes: INumberWrites;
}) {
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  const { identifiers, facts, pendingLookups, participantId } = participant;
  const heldRegistryNumbers = registryNumbers(identifiers);
  const internalIds = identifiers.filter(
    (identifier) => !VERIFIABLE_ID_TYPES.has(identifier.idType)
  );
  const pendingOn = (identifier: IParticipantNumber) =>
    pendingLookups.filter((lookup) => asksAbout(lookup, identifier));
  const stray = pendingLookups.filter(
    (lookup) => !identifiers.some((identifier) => asksAbout(lookup, identifier))
  );
  const reasonId = React.useId();
  const notPermitted = writes.permitted ? undefined : reasonId;
  const gone = Object.entries(writes.said).filter(
    ([, entry]) =>
      entry.participantId === participantId &&
      (entry.refresh || entry.verify) &&
      !identifiers.some((identifier) => sameNumber(entry, identifier))
  );

  return (
    <div className="grid gap-6">
      {identifiers.length === 0 ? (
        <p className="text-sm">{absent('No number is held for this participant.')}</p>
      ) : (
        <>
          <div className="grid gap-3">
            <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">
              Registry numbers
            </Heading>
            {heldRegistryNumbers.length === 0 ? (
              <p className="text-sm">{absent('No registry number is held.')}</p>
            ) : (
              <>
                {notPermitted && <NotPermitted id={notPermitted} what={NUMBER_WRITES} />}
                <TableFrame>
                  <Table aria-label="Registry numbers">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Number</TableHead>
                        <TableHead>Registry</TableHead>
                        <TableHead>Last answer</TableHead>
                        <TableHead>Verified</TableHead>
                        <TableHead>Owner’s answer</TableHead>
                        <TableHead>
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    {heldRegistryNumbers.map((identifier) => (
                      <RegistryNumberRows
                        key={numberKey(participantId, identifier)}
                        participantId={participantId}
                        identifier={identifier}
                        answers={answersFor(identifier.idType, facts)}
                        pending={pendingOn(identifier)}
                        writes={writes}
                        notPermitted={notPermitted}
                      />
                    ))}
                  </Table>
                </TableFrame>
              </>
            )}
          </div>
          {internalIds.length > 0 && (
            <div className="grid gap-3">
              <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">
                Internal ids
              </Heading>
              <TableFrame>
                <Table aria-label="Internal ids">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  {internalIds.map((identifier) => (
                    <InternalIdRows
                      key={numberKey(participantId, identifier)}
                      identifier={identifier}
                      pending={pendingOn(identifier)}
                      writes={writes}
                    />
                  ))}
                </Table>
              </TableFrame>
            </div>
          )}
        </>
      )}
      {stray.length > 0 && (
        <div className="grid gap-3">
          <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">
            Waiting on numbers not held here
          </Heading>
          {stray.map((lookup) => (
            <PendingLine
              key={`${lookup.factKey} ${lookup.requestedAt}`}
              lookup={lookup}
              named
              watching={writes.watching}
              onCheckAgain={writes.onCheckAgain}
            />
          ))}
        </div>
      )}
      {gone.length > 0 && (
        <div className="grid gap-3">
          <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">
            Numbers no longer held here
          </Heading>
          <ul aria-label="Numbers no longer held here" className="grid gap-3">
            {gone.map(([key, entry]) => (
              <li key={key} className="grid gap-2 rounded-md border border-border p-4 text-sm">
                <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-muted-foreground">{idTypeLabel(entry.idType)}</span>
                  <span className="font-mono break-all">{entry.value}</span>
                </span>
                {entry.refresh && <NoteLine note={entry.refresh} />}
                {entry.verify && <NoteLine note={entry.verify} />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** What came back from a press on a number, said beside it. */
export interface INumberNote {
  /** A refusal is loud; the rest — a press answered about another number, a write whose
   *  re-read failed — are not refusals of anything, and read as notices. */
  tone: 'refused' | 'notice';
  title?: string;
  text: string;
  /** Said until the page is next read: "could not be re-read" is true only until one lands. */
  untilRead?: boolean;
}

type NoteKind = 'refresh' | 'verify';

/** What one number's presses were told, with the number and its participant — kept so that it
 *  can still be said, under "Numbers no longer held here", once the number itself has gone. */
export interface INumberNotes extends IParticipantNumber {
  participantId: string;
  refresh?: INumberNote;
  verify?: INumberNote;
}

export type SayNumber = (
  participantId: string,
  number: IParticipantNumber,
  kind: NoteKind,
  note: Nullable<INumberNote>
) => void;

/** A number as the page keys it — its participant, and the pair survey-api holds unique for a
 *  participant and every write names. The household draws several members' numbers at once. */
function numberKey(participantId: string, number: IParticipantNumber): string {
  return JSON.stringify([participantId, number.idType, number.value]);
}

/**
 * What each number's two presses were told, held by the page and keyed by
 * `(participantId, idType, value)` — so it outlives a re-read, and outlives the number, where the
 * refusal is what says why it went; and so the header's "Ask registries again" says each answer
 * where the number's own press would. A hub that lands clears only what was true until one did.
 */
export function useNumberNotes(hub: IAdminParticipantHub): {
  said: Record<string, INumberNotes>;
  say: SayNumber;
} {
  const [said, setSaid] = React.useState<Record<string, INumberNotes>>({});

  React.useEffect(() => {
    setSaid((previous) => {
      const kept: Record<string, INumberNotes> = {};
      for (const [key, entry] of Object.entries(previous)) {
        const refresh = entry.refresh?.untilRead ? undefined : entry.refresh;
        const verify = entry.verify?.untilRead ? undefined : entry.verify;
        if (refresh || verify) kept[key] = { ...entry, refresh, verify };
      }
      return kept;
    });
  }, [hub]);

  const say = React.useCallback<SayNumber>(
    (participantId, number, kind, note) =>
      setSaid((previous) => {
        const key = numberKey(participantId, number);
        const entry = previous[key] ?? {
          participantId,
          idType: number.idType,
          value: number.value
        };
        return { ...previous, [key]: { ...entry, [kind]: note ?? undefined } };
      }),
    []
  );

  return { said, say };
}

/**
 * A registry number's row, and under it — only when there is something to say — the lookups
 * waiting on it and what its presses were told. One `tbody` per number, named by it, so the two
 * rows are one thing to a screen reader and to a spec.
 */
function RegistryNumberRows({
  participantId,
  identifier,
  answers,
  pending,
  writes,
  notPermitted
}: {
  participantId: string;
  identifier: IAdminParticipantIdentifier;
  /** The answers of the registries this number is checked against (`answersFor`). */
  answers: IAdminParticipantFact[];
  pending: IAdminParticipantPendingLookup[];
  writes: INumberWrites;
  /** The id of the table's `NotPermitted` sentence; present, both presses are drawn off. */
  notPermitted?: string;
}) {
  const refresh = REFRESH_OF_ID_TYPE.get(identifier.idType);
  const registry = registryLabel(identifier.idType);
  const said = writes.said[numberKey(participantId, identifier)];
  const say = (kind: NoteKind) => (note: Nullable<INumberNote>) =>
    writes.say(participantId, identifier, kind, note);
  const waiting = pending.length > 0;

  return (
    <TableBody
      aria-label={`${idTypeLabel(identifier.idType)} ${identifier.value}`}
      className="border-b border-border last:border-b-0"
    >
      <TableRow className="border-0 hover:bg-transparent">
        <TableCell className="align-top">
          <NumberValue identifier={identifier} />
          <span className="mt-1 block text-xs text-muted-foreground">
            <span>{identifier.source}</span> · {formatDateTime(identifier.createdAt)}
          </span>
        </TableCell>
        <TableCell className="align-top whitespace-nowrap">
          {registry ?? absent('None')}
        </TableCell>
        <TableCell className="align-top">
          <LastAnswers answers={answers} registry={registry} waiting={waiting} />
        </TableCell>
        <TableCell className="align-top">
          <Verification identifier={identifier} />
        </TableCell>
        <TableCell className="align-top">
          <SelfConfirmation identifier={identifier} />
        </TableCell>
        <TableCell className="align-top">
          <div className="flex flex-col items-end gap-2">
            {refresh && (
              <span className="flex items-center gap-1">
                <RefreshControl
                  participantId={participantId}
                  identifier={identifier}
                  registry={refresh}
                  held={waiting || writes.askingAll}
                  notPermitted={notPermitted}
                  say={say('refresh')}
                  onAsked={writes.onRecheck}
                />
                <FirmChangeNote identifier={identifier} />
              </span>
            )}
            {!identifier.verifiedAt && (
              <VerifyControl
                participantId={participantId}
                identifier={identifier}
                callerEmail={writes.callerEmail}
                notPermitted={notPermitted}
                say={say('verify')}
                onVerified={writes.onVerified}
              />
            )}
          </div>
        </TableCell>
      </TableRow>
      {(waiting || said?.refresh || said?.verify) && (
        <TableRow className="border-0 hover:bg-transparent">
          <TableCell colSpan={6} className="pt-0">
            <div className="grid gap-2">
              {pending.map((lookup) => (
                <PendingLine
                  key={`${lookup.factKey} ${lookup.requestedAt}`}
                  lookup={lookup}
                  watching={writes.watching}
                  onCheckAgain={writes.onCheckAgain}
                />
              ))}
              {refresh && waiting && (
                <p className="text-muted-foreground">
                  A lookup on this number is already waiting; asking again now would only answer
                  that one.
                </p>
              )}
              {said?.refresh && <NoteLine note={said.refresh} />}
              {said?.verify && <NoteLine note={said.verify} />}
            </div>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

/** An internal id's row — what it is and where it came from, and no write: nobody verifies this
 *  system's own record (R8). A lookup naming one is still drawn on it rather than lost. */
function InternalIdRows({
  identifier,
  pending,
  writes
}: {
  identifier: IAdminParticipantIdentifier;
  pending: IAdminParticipantPendingLookup[];
  writes: INumberWrites;
}) {
  return (
    <TableBody
      aria-label={`${idTypeLabel(identifier.idType)} ${identifier.value}`}
      className="border-b border-border last:border-b-0"
    >
      <TableRow className="border-0 hover:bg-transparent">
        <TableCell className="align-top whitespace-nowrap text-muted-foreground">
          {idTypeLabel(identifier.idType)}
        </TableCell>
        <TableCell className="min-w-48 align-top">
          <CopyableValue
            value={identifier.value}
            name={`Copy ${idTypeLabel(identifier.idType)} ${identifier.value}`}
          />
        </TableCell>
        <TableCell className="align-top">{identifier.source}</TableCell>
        <TableCell className="align-top whitespace-nowrap">
          {formatDateTime(identifier.createdAt)}
        </TableCell>
      </TableRow>
      {pending.length > 0 && (
        <TableRow className="border-0 hover:bg-transparent">
          <TableCell colSpan={4} className="pt-0">
            {pending.map((lookup) => (
              <PendingLine
                key={`${lookup.factKey} ${lookup.requestedAt}`}
                lookup={lookup}
                watching={writes.watching}
                onCheckAgain={writes.onCheckAgain}
              />
            ))}
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

/** A number as it is read: its type, and its value whole, selected by one click for copying. */
function NumberValue({ identifier }: { identifier: IParticipantNumber }) {
  return (
    <span className="grid min-w-32 gap-0.5">
      <span className="text-xs whitespace-nowrap text-muted-foreground">
        {idTypeLabel(identifier.idType)}
      </span>
      <span className="font-mono text-base break-all select-all">{identifier.value}</span>
    </span>
  );
}

const ANSWER_WORDS: Record<string, string> = {
  ok: 'ok',
  not_found: 'not found',
  error: 'error'
};

const DAY_MS = 86_400_000;
const RELATIVE_DAYS = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** How long ago an answer was fetched, in whole days — "today", "yesterday", "4 days ago": an
 *  answer's age is read by the day, and its instant is under the answer itself. */
function ageOf(instant: string): string {
  const days = Math.floor((Date.now() - Date.parse(instant)) / DAY_MS);
  return RELATIVE_DAYS.format(-Math.max(days, 0), 'day');
}

/**
 * What the number's registries last said for this member, each answer by its key — "ok · 4 days
 * ago" over `brokercheck.person`, then `iapd.person` — or that it was never asked; a number no
 * registry answers for says that instead. A registry that has not answered yet about a number it
 * is asked about (a CRD's IAPD, before SR-396) has no line here, and its group under Registry
 * answers says so. The answers themselves are below, under each registry's heading.
 */
function LastAnswers({
  answers,
  registry,
  waiting
}: {
  answers: IAdminParticipantFact[];
  registry: Nullable<string>;
  waiting: boolean;
}) {
  if (registry === null) return absent('No registry answers for it.');
  if (answers.length === 0) return absent(waiting ? 'asked, not answered yet' : 'never asked');
  return (
    <ul className="grid gap-1">
      {answers.map((fact) => (
        <li key={`${fact.sourceSystem} ${fact.factKey}`} className="grid">
          <span className="whitespace-nowrap">
            {ANSWER_WORDS[fact.status] ?? fact.status} · {ageOf(fact.fetchedAt)}
          </span>
          <code className="text-xs text-muted-foreground">{fact.factKey}</code>
        </li>
      ))}
    </ul>
  );
}

/**
 * A lookup asked for and not answered yet — "pressed, waiting" — with the hand-driven twin of
 * the page's own re-reads beside it. `named` spells out the number, for a lookup shown away
 * from one.
 */
function PendingLine({
  lookup,
  named = false,
  watching,
  onCheckAgain
}: {
  lookup: IAdminParticipantPendingLookup;
  named?: boolean;
  watching: boolean;
  onCheckAgain: () => Promise<unknown>;
}) {
  const [busy, setBusy] = React.useState(false);

  const check = async () => {
    setBusy(true);
    try {
      await onCheckAgain();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-[var(--color-off-white)] p-3"
    >
      <div className="grid gap-0.5">
        <span>
          Waiting for an answer
          {named && (
            <>
              {' '}
              about {idTypeLabel(lookup.idType)} <span className="font-mono">{lookup.value}</span>
            </>
          )}
          : <code className="text-xs">{lookup.factKey}</code>, asked{' '}
          {formatDateTime(lookup.requestedAt)} — {PENDING_WORDS[lookup.status] ?? lookup.status}.
        </span>
        <span className="text-muted-foreground">
          {watching
            ? 'This page reads itself again every few seconds while it waits.'
            : 'This page has stopped reading itself again. Check again to read it now.'}
        </span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => void check()}
      >
        Check again
      </Button>
    </div>
  );
}

const PENDING_WORDS: Record<string, string> = {
  queued: 'queued, not picked up yet',
  running: 'being asked now'
};

/**
 * What a refresh's refusals mean on this page. A 400 — a half missing — cannot be sent from
 * here, since both halves are the number on screen; it falls through to the body's own words.
 * The 422 sentence is this page's only where the refusal addresses `idType` (`refreshRefusal`).
 */
const REFRESH_REFUSALS: Record<number, string> = {
  404:
    'This participant no longer holds this number — the page is out of date. Nothing was ' +
    `asked. ${OUT_OF_DATE}`,
  422:
    'The system no longer asks a registry about this type of number, so nothing was asked. ' +
    'This app’s list of what it refreshes is behind the system’s.'
};

const VERIFY_REFUSALS: Record<number, string> = {
  404:
    'This number is no longer this participant’s — the page is out of date. Nothing was ' +
    `stamped. ${OUT_OF_DATE}`
};

/**
 * A refresh's refusal in words. survey-api's 422 on this route names `idType` — the type no
 * refresh covers — and only that 422 is the drift `REFRESH_OF_ID_TYPE` exists to be caught in;
 * a 422 about anything else is said in the server's own reason rather than blamed on the table.
 */
function refreshRefusal(error: unknown, registry: string): string {
  const fallback = `Could not ask ${registry} again`;
  const aboutType = (error instanceof ApiError ? (error.problem?.problems ?? []) : []).some(
    (problem) => problem.target === 'idType'
  );
  return error instanceof ApiError && error.status === 422 && !aboutType
    ? refusalMessage(error, fallback)
    : statusRefusal(error, REFRESH_REFUSALS, fallback);
}

type AskOutcome = 'asked' | 'elsewhere' | 'refused';

/**
 * One press of "Ask … again" on one number — `POST …/registry-lookups` (E3) — and what came back
 * said beside it: nothing for a lookup made, "nothing was asked" for a lookup about another
 * number, the refusal in words. The one sequence a number's own button and the header's press
 * share, so the two say the same thing about the same answer.
 *
 * **survey-api holds a pending lookup per participant and key, not per number** (its
 * `RegistryLookupService` looks one up by `(participantId, factKey)`). So a participant with
 * two CRDs — the migration's shape — has its press on the second answered 202 with the lookup
 * on the first, and nothing asked about the second. The button cannot know that before the
 * press without the type → key mapping a second time; the answer says it, so the answer is
 * read: a lookup about another number is said beside the button as nothing asked.
 */
async function askAbout(
  participantId: string,
  identifier: IParticipantNumber,
  registry: string,
  say: (note: Nullable<INumberNote>) => void
): Promise<AskOutcome> {
  say(null);
  try {
    const lookup = await participantsApi.requestRegistryLookup(participantId, {
      idType: identifier.idType,
      value: identifier.value
    });
    if (!asksAbout(lookup, identifier)) {
      say({
        tone: 'notice',
        title: 'Not asked',
        text: `A lookup on ${lookup.value} is already waiting — nothing was asked about this number.`
      });
      return 'elsewhere';
    }
    return 'asked';
  } catch (askError) {
    say({ tone: 'refused', title: 'Not asked', text: refreshRefusal(askError, registry) });
    return 'refused';
  }
}

/** A press that landed while the page could not be read again after it. */
const ASKED_UNREAD: INumberNote = {
  tone: 'notice',
  text: 'Asked — but the page could not be re-read. Reload to see it.',
  untilRead: true
};

/**
 * "Ask … again" on one number — "Ask BrokerCheck and IAPD again" on a person's CRD, whose one
 * lookup asks both (survey-api's SR-396: IAPD rides on BrokerCheck's lookup, with no pending
 * lookup of its own), "Ask BrokerCheck again" on a firm's. It verifies nothing and pins nothing:
 * an answer lands on this page as evidence to read, and a verification is still a separate press.
 * Held while a lookup on this number is waiting, because survey-api would answer the press with
 * that one and enqueue nothing — a button that reported success for that would be claiming a
 * second question; and while the header's press is going through the numbers. No re-read after a
 * refusal, one after anything else. The DD block's Disclosures line draws it too (SR-460).
 */
export function RefreshControl({
  participantId,
  identifier,
  registry,
  held,
  notPermitted,
  say,
  onAsked,
  label
}: {
  participantId: string;
  identifier: IParticipantNumber;
  /** The registry's name as the button says it — `REFRESH_OF_ID_TYPE`'s. */
  registry: string;
  held: boolean;
  /** The id of the `NotPermitted` sentence saying why the press is off; absent, it is on. */
  notPermitted?: string;
  say: (note: Nullable<INumberNote>) => void;
  onAsked: () => Promise<boolean>;
  /** The press's own words where a page names the lookup otherwise — DD calls' *Run U4 check*. */
  label?: string;
}) {
  const [busy, setBusy] = React.useState(false);

  const ask = async () => {
    setBusy(true);
    try {
      const outcome = await askAbout(participantId, identifier, registry, say);
      if (outcome === 'refused') return;
      // Awaited, so the button stays busy until the re-read shows the lookup it just made.
      if (!(await onAsked()) && outcome === 'asked') say(ASKED_UNREAD);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={busy || held || notPermitted !== undefined}
      aria-describedby={notPermitted}
      onClick={() => void ask()}
    >
      {busy ? 'Asking…' : (label ?? `Ask ${registry} again`)}
    </Button>
  );
}

/**
 * What the answer to "Ask … again" on a person's CRD can do (SR-416, F8): the press only enqueues,
 * but survey-api's worker, answering it, reopens a submitted FA survey in an open cycle whose
 * current firms the answer changed, and mails its advisor. Said before the press, behind a "?"
 * beside it (SR-474: an explanation, not what is so now). Not
 * on a firm's CRD or a team's lead CRD: neither holds an FA survey.
 */
function FirmChangeNote({ identifier }: { identifier: IAdminParticipantIdentifier }) {
  if (identifier.idType !== 'CRD') return null;
  // An explanation, so one press away (SR-474): what the press can lead to, not what is so now.
  return (
    <span data-firm-change-note>
      <HelpPopover label="What asking again can do">
        <p>
          When the registries answer, a submitted FA survey whose firms changed is reopened and its
          advisor mailed.
        </p>
      </HelpPopover>
    </span>
  );
}

/**
 * A registry number's "Ask … again" away from its row — under its registry's answers (R9). The
 * same press as the row's, held the same way, and what it was told read from the same note, so it
 * is said here and beside the number alike. Nothing for a type no refresh reaches.
 */
export function NumberRefresh({
  participantId,
  identifier,
  pending,
  writes
}: {
  participantId: string;
  identifier: IAdminParticipantIdentifier;
  /** The member's pending lookups; the ones on this number hold the button. */
  pending: IAdminParticipantPendingLookup[];
  writes: INumberWrites;
}) {
  const reasonId = React.useId();
  const refresh = REFRESH_OF_ID_TYPE.get(identifier.idType);
  if (!refresh) return null;
  const said = writes.said[numberKey(participantId, identifier)];
  const waiting = pending.some((lookup) => asksAbout(lookup, identifier));
  return (
    <div
      role="group"
      aria-label={`Ask again: ${idTypeLabel(identifier.idType)} ${identifier.value}`}
      className="grid gap-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-muted-foreground">{idTypeLabel(identifier.idType)}</span>
        <span className="font-mono break-all">{identifier.value}</span>
        <RefreshControl
          participantId={participantId}
          identifier={identifier}
          registry={refresh}
          held={waiting || writes.askingAll}
          notPermitted={writes.permitted ? undefined : reasonId}
          say={(note) => writes.say(participantId, identifier, 'refresh', note)}
          onAsked={writes.onRecheck}
        />
        <FirmChangeNote identifier={identifier} />
      </div>
      {!writes.permitted && <NotPermitted id={reasonId} what="asking a registry again" />}
      {waiting && (
        <p className="text-muted-foreground">A lookup on this number is already waiting.</p>
      )}
      {said?.refresh && <NoteLine note={said.refresh} />}
    </div>
  );
}

interface IAskTarget {
  participantId: string;
  identifier: IAdminParticipantIdentifier;
  registry: string;
}

/** What the header's "Ask registries again" works from, and its press. */
export interface IAskEveryNumber {
  /** The household's numbers a refresh covers — none, and the header draws no button. */
  covered: number;
  /** Those of them nothing waits on: the ones a press would ask about. */
  targets: IAskTarget[];
  askingAll: boolean;
  askAll: () => Promise<void>;
}

/** Identity & numbers' anchor — where every number's answer is said; the registry names it so. */
export const IDENTITY_ANCHOR = 'identity';

/**
 * "Ask registries again" (R8): the press of "Ask … again" on every number of the household a
 * refresh covers and nothing waits on — each said beside its own number exactly as that number's
 * own press would be — then **one** re-read, which restarts the page's re-read window once
 * rather than per number. A number already waiting is left out, as its own button is held:
 * survey-api would answer it with the lookup that waits and ask nothing.
 *
 * **In turn, not at once.** survey-api holds one pending lookup per participant and key, so two
 * presses on one member's numbers at once would race for which of them it holds, and what is said
 * beside each would depend on the race.
 */
export function useAskEveryNumber(
  members: IAdminParticipantDetail[],
  say: SayNumber,
  onAsked: () => Promise<boolean>
): IAskEveryNumber {
  const [askingAll, setAskingAll] = React.useState(false);
  const covered = members.flatMap((member) =>
    member.identifiers.flatMap((identifier) => {
      const registry = REFRESH_OF_ID_TYPE.get(identifier.idType);
      return registry === undefined
        ? []
        : [{ participantId: member.participantId, identifier, registry, member }];
    })
  );
  const targets = covered
    .filter(
      ({ member, identifier }) =>
        !member.pendingLookups.some((lookup) => asksAbout(lookup, identifier))
    )
    .map(({ participantId, identifier, registry }) => ({ participantId, identifier, registry }));

  const askAll = async () => {
    // Each answer is said beside its number, under Identity & numbers — a fold a viewer may have
    // shut on another record (the memory is per viewer, not per record), where a refusal would be
    // said to nobody. Opened for the visit, as the nav and "Write a note" open theirs.
    const identity = document.getElementById(IDENTITY_ANCHOR);
    if (identity) openFolds(identity);
    setAskingAll(true);
    try {
      const asked: IAskTarget[] = [];
      let answered = false;
      for (const target of targets) {
        const sayHere = (note: Nullable<INumberNote>) =>
          say(target.participantId, target.identifier, 'refresh', note);
        const outcome = await askAbout(
          target.participantId,
          target.identifier,
          target.registry,
          sayHere
        );
        if (outcome !== 'refused') answered = true;
        if (outcome === 'asked') asked.push(target);
      }
      if (!answered || (await onAsked())) return;
      for (const target of asked) {
        say(target.participantId, target.identifier, 'refresh', ASKED_UNREAD);
      }
    } finally {
      setAskingAll(false);
    }
  };

  return { covered: covered.length, targets, askingAll, askAll };
}

/** The header's button, and how many numbers it would ask about — or why it asks about none. */
export function AskEveryNumber({
  asking,
  permitted
}: {
  asking: IAskEveryNumber;
  /** Whether the reader holds `responses.annotate` (SR-228); without it the press is drawn off. */
  permitted: boolean;
}) {
  const reasonId = React.useId();
  if (asking.covered === 0) return null;
  const count = asking.targets.length;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={asking.askingAll || count === 0 || !permitted}
        aria-describedby={permitted ? undefined : reasonId}
        onClick={() => void asking.askAll()}
      >
        {asking.askingAll ? 'Asking…' : 'Ask registries again'}
      </Button>
      {!permitted && <NotPermitted id={reasonId} what="asking registries again" />}
      <span className="text-muted-foreground">
        {count === 0
          ? 'A lookup is already waiting on every number a registry can be asked about again.'
          : `${count === 1 ? 'One number' : `${count} numbers`} to ask about — what each is ` +
            'told is said beside it, under Identity & numbers.'}
      </span>
    </div>
  );
}

/**
 * `POST …/identifier-verifications` (A6) — stamp this number verified by hand.
 *
 * Confirmed first, because it cannot be taken back: survey-api only ever sets `verified_at`
 * and no route clears it, so the confirmation names the number, says that, and says whose name
 * goes on it. Offered only on a number with no stamp; a stamped one shows who and how instead.
 * An owner's denial is repeated in the confirmation — it does not stop the stamp, which is
 * survey-api's to refuse and it does not, but it is the one thing the press should not be
 * made without seeing.
 */
function VerifyControl({
  participantId,
  identifier,
  callerEmail,
  notPermitted,
  say,
  onVerified
}: {
  participantId: string;
  identifier: IAdminParticipantIdentifier;
  callerEmail: string;
  /** The id of the `NotPermitted` sentence saying why the press is off; absent, it is on. */
  notPermitted?: string;
  say: (note: Nullable<INumberNote>) => void;
  onVerified: () => Promise<boolean>;
}) {
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const label = idTypeLabel(identifier.idType);

  const verify = async () => {
    setBusy(true);
    say(null);
    try {
      await participantsApi.verifyIdentifier(participantId, {
        idType: identifier.idType,
        value: identifier.value
      });
      // The stamp is made whatever the re-read does; a re-read that fails leaves this number
      // drawn unverified, and the button would be the one thing on the page saying otherwise.
      if (!(await onVerified())) {
        say({
          tone: 'notice',
          text: 'Verified — but the page could not be re-read. Reload to see it.',
          untilRead: true
        });
      }
    } catch (verifyError) {
      say({
        tone: 'refused',
        title: 'Not verified',
        text: statusRefusal(verifyError, VERIFY_REFUSALS, 'Could not verify this number')
      });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || notPermitted !== undefined}
          aria-describedby={notPermitted}
        >
          Verify number
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Verify {label} {identifier.value}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This records the number as verified by hand, now, by you
            {callerEmail ? ` (${callerEmail})` : ''}. It cannot be undone — no route clears a
            verification. Verify it only after reading the registry yourself.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {identifier.selfConfirmation === 'denied' && (
          <p className="text-sm font-medium text-destructive">
            Its owner said this number is not theirs.
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(event) => {
              // Held open until the answer is in, so a slow stamp is not a closed dialog and
              // a button that looks as if nothing happened.
              event.preventDefault();
              void verify();
            }}
          >
            {busy ? 'Verifying…' : 'Verify it'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Verification({ identifier }: { identifier: IAdminParticipantIdentifier }) {
  if (!identifier.verifiedAt) return absent('Not verified.');

  return (
    <>
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[var(--color-royal-blue)]">
          <BadgeCheck className="size-3.5" aria-hidden />
          Verified {formatDateTime(identifier.verifiedAt)}
        </span>
        {identifier.verifiedVia && <Badge variant="soft">{identifier.verifiedVia}</Badge>}
      </span>
      <span className="mt-1 flex items-center gap-1 text-muted-foreground">
        {verifiedBy(identifier)}
        {identifier.verifiedVia === 'registry' && (
          <HelpPopover label="About a registry’s verification">
            <p>The registry named somebody for it, and the registrant confirmed that was them.</p>
          </HelpPopover>
        )}
      </span>
    </>
  );
}

/** Who stands behind a stamp. `verifiedBy` is set for a staff stamp alone — a registry stamp
 *  has no person behind it — so `verifiedVia` is what tells the two apart. */
function verifiedBy(identifier: IAdminParticipantIdentifier): string {
  switch (identifier.verifiedVia) {
    case 'registry':
      return 'By the registry, confirmed by the registrant.';
    case 'staff':
      return identifier.verifiedBy
        ? `By ${identifier.verifiedBy.email}, by hand.`
        : 'By a member of staff, by hand.';
    default:
      // Closed in the type, open on the wire: a source survey-api adds later is still a stamp.
      return identifier.verifiedVia
        ? `By ${identifier.verifiedVia}.`
        : 'By an unrecorded source.';
  }
}

function SelfConfirmation({ identifier }: { identifier: IAdminParticipantIdentifier }) {
  switch (identifier.selfConfirmation) {
    case 'denied':
      return (
        <>
          <Badge variant="destructive">Denied</Badge>
          <span className="mt-1 block">The owner said this number is not theirs.</span>
        </>
      );
    case 'confirmed':
      return 'The owner confirmed it is theirs.';
    default:
      return absent('Not answered yet.');
  }
}
